import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  /** A button that gets the user out of the empty state. */
  action?: ReactNode;
  className?: string;
}

/**
 * "There is genuinely nothing here."
 *
 * Render this only when a request SUCCEEDED and came back empty. If the request
 * failed, use <ErrorState>; if it is still in flight, use <Skeleton>. On the
 * Today screen this distinction is a safety property, not a nicety: an empty
 * state shown after a failed fetch tells someone they have no medication to
 * take today.
 *
 * Copy rule (CONTRACT §7): no medical advice here either. "Nothing scheduled
 * today", not "You're all caught up, nothing to worry about".
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-card border border-dashed border-line',
        'px-6 py-12 text-center',
        className,
      )}
    >
      <span className="flex size-12 items-center justify-center rounded-full bg-raised text-muted">
        <Icon className="size-6" aria-hidden="true" />
      </span>
      <div className="space-y-1">
        <p className="text-base font-medium text-ink">{title}</p>
        {description ? (
          <p className="mx-auto max-w-sm text-sm leading-6 text-muted">{description}</p>
        ) : null}
      </div>
      {action ? <div className="pt-1">{action}</div> : null}
    </div>
  );
}
